import type { CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '../../components/Icon';
import type { Tint } from '../../shell/nav';

// La cornice di ogni widget della regia: poche righe che si capiscono a colpo d'occhio, e un
// rimando ai dettagli sempre nello stesso angolo.
export function Widget({
  title,
  icon,
  count,
  detailsTo,
  detailsLabel = 'Dettagli',
  children,
  style,
  tone,
  tint = 'lilac',
}: {
  title: string;
  icon: string;
  count?: string | number;
  detailsTo?: string;
  detailsLabel?: string;
  children: ReactNode;
  style?: CSSProperties;
  tone?: 'warn';
  /** il colore pop del riquadro (tela «PopTown Pop Style»): icona e numero */
  tint?: Tint;
}) {
  return (
    <section
      aria-label={title}
      className="dash-widget"
      data-tint={tone === 'warn' ? 'pink' : tint}
      data-tone={tone}
      style={style}
    >
      <header className="dash-widget-head">
        <span className="dash-widget-icon" aria-hidden>
          <Icon name={icon} size={20} />
        </span>
        <h2 className="dash-widget-title">{title}</h2>
        {count !== undefined && (
          <span className="dash-widget-count" data-zero={!Number(count) || undefined}>
            {count}
          </span>
        )}
        <span style={{ flex: 1 }} />
        {detailsTo && (
          <Link to={detailsTo} className="dash-more">
            {detailsLabel}
          </Link>
        )}
      </header>
      {children}
    </section>
  );
}

/** una riga di elenco compatta: icona, testo principale, nota a destra */
export function WidgetRow({
  icon,
  iconColor,
  main,
  sub,
  right,
  onClick,
}: {
  icon: string;
  iconColor?: string;
  main: ReactNode;
  sub?: ReactNode;
  right?: ReactNode;
  onClick?: () => void;
}) {
  const body = (
    <>
      <span style={{ color: iconColor ?? 'var(--faint)', display: 'inline-flex', marginTop: 2 }}>
        <Icon name={icon} size={14} />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span
          className="t-small"
          style={{
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {main}
        </span>
        {sub && (
          <span
            className="t-caption faint"
            style={{
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {sub}
          </span>
        )}
      </span>
      {right && (
        <span className="t-caption faint" style={{ whiteSpace: 'nowrap' }}>
          {right}
        </span>
      )}
    </>
  );
  const style: CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: '6px 4px',
    borderRadius: 'var(--r-sm)',
    width: '100%',
    textAlign: 'left',
  };
  return onClick ? (
    <button
      type="button"
      onClick={onClick}
      style={{ ...style, border: 'none', background: 'none', color: 'inherit', cursor: 'pointer' }}
    >
      {body}
    </button>
  ) : (
    <div style={style}>{body}</div>
  );
}
